import React from "react";
import Drawer from "components/Drawer";

/**
 * Wrapper fino sobre el Drawer reutilizable para integrarlo con el sistema
 * Redux de modales (showModal / hideModal).
 *
 * modalProps soportadas:
 *  - content: ReactNode | (args) => ReactNode
 *      Contenido que se renderizará DENTRO del drawer. Si es una función, se
 *      invoca con { close, registerBeforeClose } para permitir que el hijo
 *      cierre el drawer y registre un "beforeClose" para pedir confirmación
 *      (por ejemplo cuando hay cambios sin guardar).
 *  - width, closeOnOverlayClick, closeOnEsc, className, style: reenviados al Drawer
 *  - shouldConfirmClose, confirmCloseMessage: guardas estáticas de cierre
 *
 * Patrón recomendado para confirmación dinámica (dirty-aware):
 *   showModal({
 *     open: true,
 *     width: 1000,
 *     content: ({ close, registerBeforeClose }) => (
 *       <MyDrawerContent
 *         handleClose={close}
 *         registerBeforeClose={registerBeforeClose}
 *       />
 *     ),
 *   }, 'drawer')
 *
 * Nota: Modal/index.js detecta el modalType "drawer" y omite el wrapper de
 * ReactModal ya que el Drawer gestiona su propio portal + backdrop.
 */
class DrawerModal extends React.Component {
	constructor(props) {
		super(props);
		this.beforeCloseRef = { current: null };
		this.registerBeforeClose = (fn) => {
			this.beforeCloseRef.current = fn;
			return () => {
				if (this.beforeCloseRef.current === fn) {
					this.beforeCloseRef.current = null;
				}
			};
		};
	}

	handleClose = () => {
		const beforeClose = this.beforeCloseRef.current;
		if (typeof beforeClose === "function") {
			const result = beforeClose();
			if (result === false) return;
		}
		if (typeof this.props.closeModal === "function") this.props.closeModal();
	};

	render() {
		const {
			content,
			width,
			closeOnOverlayClick,
			closeOnEsc,
			shouldConfirmClose,
			confirmCloseMessage,
			className,
			style,
		} = this.props;

		const renderedContent =
			typeof content === "function"
				? content({
						close: this.handleClose,
						registerBeforeClose: this.registerBeforeClose,
				  })
				: content;

		return (
			<Drawer
				isOpen={true}
				onClose={this.handleClose}
				width={width}
				closeOnOverlayClick={closeOnOverlayClick}
				closeOnEsc={closeOnEsc}
				shouldConfirmClose={shouldConfirmClose}
				confirmCloseMessage={confirmCloseMessage}
				className={className}
				style={style}
			>
				{renderedContent}
			</Drawer>
		);
	}
}

export default DrawerModal;
